import styled from '@emotion/styled';
import React from 'react';
import { observer } from 'mobx-react';
import { ITheme } from '../../../../../ui';
import { useTranslation } from '../../../../shared/i18n/translate/useTranslation';
import { BookingDate } from '../../booking/BookingDate';
import { BookingMapLink } from '../../booking/BookingMapLink';
import { BookingQr } from '../../booking/BookingQr';
import { BookingStatus } from '../../booking/BookingStatus';
import {
	StyledBookingDetailsCell as Cell,
	StyledBookingDetailsRow as Row,
	StyledBookingDetailsTable as Table,
	StyledBookingDetailsTableBody as TableBody,
} from '../../booking/BookingDetailsTable';
import { IBookingDetailsProps } from './BookingDetails';
import { getDaysInTotalDetails } from '../../../domain/utils/getDaysInTotalDetails';
import themed from '../../../../../ui/themer/themed';
import { createBookingDateTimeString } from '../helpers/createBookingDateTimeString';

export const BookingDetailsTable = observer(
	({ booking }: IBookingDetailsProps) => {
		const { t } = useTranslation();
		const {
			reference,
			status,
			parkingProduct,
			daysInTotal,
			parkingProductsQuote: { startDate, startTime, endDate, endTime },
		} = booking;

		return (
			<Table>
				<TableBody>
					<Row>
						<Cell>{`${t('parking_booking_details_reference')}:`}</Cell>
						<Cell bold>{reference}</Cell>
						<Cell>
							<BookingStatus status={status} />
						</Cell>
					</Row>
					<StyledQrRow>
						<Cell>{`${t('parking_booking_details_qr_code')}:`}</Cell>
						<Cell colSpan={2}>
							<BookingQr reference={reference} />
						</Cell>
					</StyledQrRow>
					<StyledParkingProductRow>
						<Cell>{`${t('parking_booking_details_parking_location')}:`}</Cell>
						<Cell colSpan={2}>
							<StyledParkingLocation>
								<div>{parkingProduct.name ?? ''}</div>
								{daysInTotal && getDaysInTotalDetails(daysInTotal, t)}
							</StyledParkingLocation>
							{parkingProduct.coordinates && (
								<BookingMapLink
									coordinates={parkingProduct.coordinates}
									description={parkingProduct.name || ''}
								/>
							)}
						</Cell>
					</StyledParkingProductRow>
					<Row>
						<Cell>{`${t('parking_booking_entry_date')}:`}</Cell>
						<Cell bold colSpan={2}>
							<BookingDate
								date={createBookingDateTimeString(startDate, startTime)}
								selectedTime={startTime}
							/>
						</Cell>
					</Row>
					<Row>
						<Cell>{`${t('parking_booking_exit_date')}:`}</Cell>
						<Cell bold colSpan={2}>
							<BookingDate
								date={createBookingDateTimeString(endDate, endTime)}
								selectedTime={endTime}
							/>
						</Cell>
					</Row>
				</TableBody>
			</Table>
		);
	},
);

const StyledParkingLocation = styled.div`
	font-weight: 700;
`;
export const StyledQrRow = styled(Row)`
	border-bottom: none;

	${Cell} {
		vertical-align: top;
	}
`;

export const StyledParkingProductRow = styled(Row)<{}, ITheme>`
	background: ${themed(({ background }) => background.light)};
	border-bottom: none;
`;
